import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth';
import { apiRequest } from '@/lib/queryClient';
import { KG, DESKTOP_NAV } from './desktop-tokens';
import TelegramImage from '@/components/profile/telegram-image';
import { useDetailPanel } from './detail-panel-context';

interface DesktopSidebarProps {
  collapsed?: boolean;
  width?: number;
  unreadChatCount?: number;
}

export function DesktopSidebar({
  collapsed = false,
  width = 240,
  unreadChatCount = 0,
}: DesktopSidebarProps) {
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  const { open: openDetailPanel } = useDetailPanel();

  const rawUsername = (user as any)?.username || (user as any)?.firstName || 'me';
  const displayName = rawUsername.charAt(0).toUpperCase() + rawUsername.slice(1);
  const userId = (user as any)?.id as number | undefined;

  // /api/auth/me doesn't include photos, so fetch them separately for the avatar.
  const { data: userPhotosData } = useQuery({
    queryKey: ['/api/users/photos'],
    queryFn: async () => {
      try {
        return await apiRequest('GET', '/api/users/photos');
      } catch {
        return { photos: [], hasValidPhotos: false };
      }
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
  const userPhotos = (userPhotosData as any)?.photos as Array<{ id?: number; url?: string }> | undefined;

  // Organizer panel is only relevant to users who own at least one group.
  const { data: ownedGroupsData } = useQuery({
    queryKey: ['/api/groups/owned'],
    queryFn: async () => {
      try {
        return await apiRequest('GET', '/api/groups/owned');
      } catch {
        return { groups: [] };
      }
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
  const ownsGroups = (((ownedGroupsData as any)?.groups) || []).length > 0;
  const isAdmin = !!(user as any)?.isAdmin;
  const subline = [
    (user as any)?.profile_type || (user as any)?.profileType,
    (user as any)?.city,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <aside
      style={{
        width: collapsed ? 72 : width,
        flexShrink: 0,
        background: KG.bg,
        borderRight: `1px solid ${KG.border}`,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        position: 'sticky',
        top: 0,
      }}
    >
      <div
        style={{
          padding: collapsed ? '10px 8px' : 12,
          borderBottom: `1px solid ${KG.border}`,
        }}
      >
        <button
          type="button"
          onClick={() => {
            if (userId != null) openDetailPanel({ kind: 'profile', id: userId });
          }}
          disabled={userId == null}
          title={displayName}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: collapsed ? 8 : 10,
            borderRadius: 12,
            background: KG.panel,
            justifyContent: collapsed ? 'center' : 'flex-start',
            border: 'none',
            width: '100%',
            textAlign: 'left',
            color: 'inherit',
            cursor: userId != null ? 'pointer' : 'default',
            fontFamily: 'inherit',
          }}
        >
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 99,
              overflow: 'hidden',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 0 2px ${KG.bg}, 0 0 0 4px rgba(37,101,235,0.4)`,
              flexShrink: 0,
              background: 'linear-gradient(135deg,#3a3a42,#1d1d22)',
            }}
          >
            <TelegramImage
              photos={userPhotos}
              userId={userId}
              username={rawUsername}
              variant="avatar"
              alt={rawUsername}
              className="w-full h-full"
            />
          </span>
          {!collapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayName}
                {(user as any)?.isVip && (
                  <span
                    style={{
                      fontSize: 10,
                      padding: '1px 5px',
                      borderRadius: 4,
                      background: 'linear-gradient(180deg,#f5c542,#d59e1c)',
                      color: '#1a1102',
                      fontWeight: 800,
                      letterSpacing: '0.04em',
                    }}
                  >
                    VIP
                  </span>
                )}
              </div>
              {subline && (
                <div
                  style={{
                    fontSize: 11,
                    color: KG.hint,
                    marginTop: 2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {subline}
                </div>
              )}
            </div>
          )}
        </button>
      </div>

      <nav
        style={{
          padding: collapsed ? '12px 8px' : 12,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: 1,
          overflowY: 'auto',
        }}
      >
        {!collapsed && (
          <div
            style={{
              padding: '10px 12px 4px',
              fontSize: 11,
              fontWeight: 600,
              color: KG.hint2,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            Naviga
          </div>
        )}

        {DESKTOP_NAV.map((item) => {
          const active = item.matches(location);
          const badge = item.id === 'chat' ? unreadChatCount : 0;
          return (
            <button
              key={item.id}
              type="button"
              title={collapsed ? item.label : undefined}
              onClick={() => setLocation(item.route)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: collapsed ? 12 : '10px 12px',
                justifyContent: collapsed ? 'center' : 'flex-start',
                borderRadius: 12,
                cursor: 'pointer',
                position: 'relative',
                background: active ? KG.cardHi : 'transparent',
                color: active ? KG.text : KG.hint,
                border: 'none',
                width: '100%',
                textAlign: 'left',
                fontFamily: 'inherit',
              }}
            >
              <span
                style={{
                  width: 26,
                  height: 26,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 18,
                  filter: `drop-shadow(0 0 4px rgba(${item.glow},${active ? 0.6 : 0.25}))`,
                }}
              >
                {item.emoji}
              </span>
              {!collapsed && (
                <span style={{ fontSize: 14, fontWeight: active ? 600 : 500 }}>
                  {item.label}
                </span>
              )}
              {!collapsed && badge > 0 && (
                <span
                  style={{
                    marginLeft: 'auto',
                    minWidth: 20,
                    height: 20,
                    padding: '0 6px',
                    background: KG.red,
                    color: '#fff',
                    borderRadius: 99,
                    fontSize: 11,
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
              {collapsed && badge > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    minWidth: 16,
                    height: 16,
                    padding: '0 4px',
                    background: KG.red,
                    color: '#fff',
                    borderRadius: 99,
                    fontSize: 10,
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
              {active && !collapsed && (
                <span
                  style={{
                    position: 'absolute',
                    left: -12,
                    top: 8,
                    bottom: 8,
                    width: 3,
                    borderRadius: 99,
                    background: KG.blue,
                  }}
                />
              )}
            </button>
          );
        })}

        {!collapsed && ownsGroups && (
          <button
            type="button"
            onClick={() => setLocation('/organizer')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginTop: 6,
              padding: '10px 12px',
              borderRadius: 12,
              color: location.startsWith('/organizer') ? KG.text : KG.hint,
              fontSize: 14,
              fontWeight: location.startsWith('/organizer') ? 600 : 500,
              background: location.startsWith('/organizer') ? KG.cardHi : 'transparent',
              border: 'none',
              width: '100%',
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <span
              style={{
                width: 26,
                height: 26,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                opacity: 0.85,
              }}
            >
              📋
            </span>
            <span>Organizzatore</span>
          </button>
        )}

        {isAdmin && (
          <button
            type="button"
            title={collapsed ? 'Admin' : undefined}
            onClick={() => setLocation('/admin')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginTop: 6,
              padding: collapsed ? 12 : '10px 12px',
              justifyContent: collapsed ? 'center' : 'flex-start',
              borderRadius: 12,
              color: location.startsWith('/admin') ? KG.text : KG.hint,
              fontSize: 14,
              fontWeight: location.startsWith('/admin') ? 600 : 500,
              background: location.startsWith('/admin') ? KG.cardHi : 'transparent',
              border: 'none',
              width: '100%',
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <span
              style={{
                width: 26,
                height: 26,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                opacity: 0.85,
              }}
            >
              🛡️
            </span>
            {!collapsed && <span>Admin</span>}
          </button>
        )}
      </nav>

      <button
        type="button"
        onClick={() => setLocation('/discover')}
        style={{
          height: 64,
          padding: collapsed ? 0 : '0 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderTop: `1px solid ${KG.border}`,
          background: 'transparent',
          border: 'none',
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
          color: KG.text,
          cursor: 'pointer',
          width: '100%',
        }}
      >
        <img
          src="/assets/images/brand/icon-256-transparent.webp"
          alt="Kinkygram"
          width={36}
          height={36}
          style={{
            width: 36,
            height: 36,
            objectFit: 'contain',
            flexShrink: 0,
          }}
        />
        {!collapsed && (
          <span style={{ fontSize: 17, fontWeight: 700, letterSpacing: '-0.01em' }}>
            kinkygram
          </span>
        )}
      </button>
    </aside>
  );
}

export default DesktopSidebar;
